import React, { useEffect, useState } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { makeStyles } from '@mui/styles';
import { StarFilled } from '@ant-design/icons';

import {
  AdminOptionCats,
  Credit,
  CreditNbfiStatus,
  creditNbfiStatusOptions,
  CreditStatus,
  VERQOR_BANK_ID,
} from '@types';
import {
  AutoCompleteReferenceInput,
  InitialCard,
  InitialRow,
  NumberSuffixInput,
  ReferenceInput,
  SelectInput,
} from '@components';
import { adminSettingsHttp } from '@network';
import { centsFormatter, rbac } from '@utils';
import { getCreditStatusOptions } from '@pages/credit/CreditStatusField';
import cls from './CreditInfo.module.css';

interface Option {
  id: number;
  name: string;
}

const Rating = ({ rating }: {rating: number}) => {
  return (
    <div className={cls.ratingWrap}>
      <div className={cls.ratingValue}>Rate: <b>{rating}/10</b></div>
      <div className={cls.ratingIconBlock}>
        <StarFilled className={cls.ratingIcon} />
      </div>
    </div>
  );
};

export const CreditInfo = ({ credit, editable }: {credit: Credit, editable: boolean}) => {
  const cls = useStyles();
  const [reasons, setReasons] = useState<Option[]>([]);
  const status = useWatch({ name: 'status' });
  const bankId = useWatch({ name: 'bankId' });
  const {
    field: { value: nbfiStatus, onChange: changeNbfiStatus },
  } = useController({ name: 'nbfiStatus' });
  const rejectReasonId = useWatch({ name: 'rejectReasonId' });
  const [rejectOptions, setRejectOptions] = useState<Option[]>([]);
  const [nbfiStatusInitial, setNbfiStatusInitial] = useState(credit.nbfiStatus);

  const rejectReasonEnabled = status === CreditStatus.DENIED;
  const showNbfiStatus = bankId && bankId !== VERQOR_BANK_ID;

  const reason = reasons.find(i => i.id === rejectReasonId);
  const isScoreVerqorReason = reason?.name === 'Score Verqor';

  useEffect(() => {
    if (bankId
      && bankId !== VERQOR_BANK_ID
      && !nbfiStatusInitial
      && nbfiStatus !== CreditNbfiStatus.PENDING) {
      changeNbfiStatus(CreditNbfiStatus.PENDING);
      setNbfiStatusInitial(CreditNbfiStatus.PENDING);
    }
  }, [nbfiStatus, bankId, nbfiStatusInitial]);

  useEffect(() => {
    (async () => {
      const [allReasons, allRejectOptions] = await Promise.all([
        adminSettingsHttp.getOptionsByCategory(AdminOptionCats.SALES_CREDITS_REASON_FOR_REJECTION),
        adminSettingsHttp.getOptionsByCategory(AdminOptionCats.SALES_CREDITS_RISK_REJECTION_REASON),
      ]);
      setReasons(allReasons.map(i => ({
        id: i.id as number,
        name: i.name,
      })));
      setRejectOptions(allRejectOptions.map(i => ({
        id: i.id as number,
        name: i.name,
      })));
    })();
  }, []);

  const statusOptions = getCreditStatusOptions();
  const hasStatusControl = rbac.hasCreditStatusControl();

  let isStatusDisabled = !editable;
  if (hasStatusControl) {
    isStatusDisabled = false;
  }
  if (credit.isDenied) {
    isStatusDisabled = true;
  }

  return (
    <InitialCard
      left="Credit"
      right={<Rating rating={credit.rating} />}
      autoHeight
      extraPadding
    >
      <div className={cls.box}>
        <InitialRow title="Code:">
          {credit.code}
        </InitialRow>
        <InitialRow title="Balance:">
          {credit.balanceTitle}
        </InitialRow>
        <InitialRow title="Requested Amount:">
          <NumberSuffixInput source="requestAmount" suffix="$" fractional light
            min={150000} max={3000000}
            helperText="150,000 — 3,000,000"
            hidden={credit.isUsd}
            className={credit.isUsd ? cls.hidden : undefined}
            readOnly={!editable}
          />
          <NumberSuffixInput source="usd_requestAmount" suffix="$ USD" fractional light
            min={8670} max={173400}
            helperText="8,670 — 173,400"
            className={!credit.isUsd ? cls.hidden : undefined}
            readOnly={!editable}
          />
        </InitialRow>
        <InitialRow title="Status:">
          <SelectInput
            label={false}
            source="status"
            choices={statusOptions}
            required
            disabled={isStatusDisabled}
            readOnly={!editable}
          />
        </InitialRow>
        <InitialRow title={`Reason for rejection${rejectReasonEnabled ? '*' : ''}:`}>
          <SelectInput
            label={false}
            source="rejectReasonId"
            disabled={!rejectReasonEnabled || !editable}
            required={rejectReasonEnabled && editable}
            choices={reasons}
            readOnly={!editable}
          />
        </InitialRow>
        <InitialRow title="Risk rejection reason:">
          {isScoreVerqorReason ? (
            <SelectInput
              label={false}
              source="riskRejectReasonId"
              choices={rejectOptions}
            />
          ) : credit.riskRejectReason}
        </InitialRow>
        <InitialRow title="Status before rejection:">
          <SelectInput
            label={false}
            source="statusBeforeRejection"
            choices={statusOptions}
            disabled
          />
        </InitialRow>
        <InitialRow title="Bank:">
          <ReferenceInput reference="bank" source="bankId" allowEmpty>
            <SelectInput
              optionText="nameMx"
              label={false}
              readOnly={!editable}
              disabled={!editable}
            />
          </ReferenceInput>
        </InitialRow>

        {showNbfiStatus ? (
          <InitialRow title="NBFI Status:">
            <SelectInput
              label={false}
              source="nbfiStatus"
              choices={creditNbfiStatusOptions}
              readOnly={!editable}
            />
          </InitialRow>
        ) : (
          <InitialRow title="Total Orders:">
            {centsFormatter.format(credit.isUsd
              ? credit.usd_ordersTotalAmount
              : credit.ordersTotalAmount)}
            {credit.isUsd && ' USD'}
          </InitialRow>
        )}

        <InitialRow title="Collections Manager:">
          <ReferenceInput reference="admin/collectors" source="collectionsManagerId" allowEmpty>
            <SelectInput optionText="name" label={false} disabled={!editable} />
          </ReferenceInput>
        </InitialRow>
        <InitialRow title="Withholding Company:">
          <AutoCompleteReferenceInput
            source="mainBuyerName"
            reference="/dealer/by-type/buyer"
            placeholder="Search Buyers"
            readOnly={!editable}
          />
        </InitialRow>
      </div>
    </InitialCard>
  );
};

const useStyles = makeStyles({
  hidden: {
    display: 'none',
  },
  box: {
    marginTop: 15,
    fontFamily: 'var(--font-primary)',
    color: 'var(--text-color)',
    '& .MuiSelect-select.Mui-disabled': {
      '-webkit-text-fill-color': 'unset !important',
    },
  },
  footer: {
    marginTop: 25,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  exportBtn: {
    borderRadius: 8,
    border: '1px solid var(--primary-color)',
    background: 'var(--foreground-color)',
    width: '254px !important',
    height: 37,
    color: 'var(--primary-color)',
    fontFamily: 'var(--font-primary)',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: 400,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    '&[disabled] path:not(:last-child)': {
      fill: 'grey',
    },
  },
});
